import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ENTRY_ORDER,
  LIMIT_ORDER_TYPE,
  STOP_LIMIT_ORDER_TYPE,
  STOP_MARKET_ORDER_TYPE,
  TAKE_PROFIT_ORDER,
} from "common/constants";
import OrderTypeSelector from "features/portfolio/CreateOrder/OrderInfo/OrderTypeSelector";
import AmountInput from "features/portfolio/CreateOrder/AmountInput";
import {
  AMOUNT_ERROR,
  LIMIT_PRICE_ERROR,
  TRIGGER_PRICE_ERROR,
} from "features/portfolio/CreateOrder/constants";
import TriggerPriceInput from "features/portfolio/CreateOrder/OrderInfo/TriggerPriceInput";
import LimitPriceInput from "features/portfolio/CreateOrder/OrderInfo/LimitPriceInput";
import GeneralInfoBoxes from "features/portfolio/CreateOrder/OrderInfo/GeneralInfoBoxes";
import SymbolAndExchangeSummary from "features/portfolio/CreateOrder/OrderInfo/SymbolAndExchangeSummary";
import useGeneralConfiguration from "features/configuration/GeneralConfiguration/useGeneralConfiguration";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Space from "antd/es/space";
import Button from "common/Buttons/Button";
import Flex from "common/Boxes/Flex";
import { toFixed } from "@shared/utils/numbers";
import { displayResultMessage, openMessage } from "utils/misc";
import { getItemMaxOptionValue } from "features/configuration/GeneralConfiguration/utils";
import usePrecision from "@shared/common/Precision/usePrecision";
import {
  ConfigurationItem,
  CreateOrderData,
  CreateOrderEntryOrTakeProfit,
  LanguageNameSpaces,
  LongShort,
  MarginTypes,
  OrderType,
  PrecisionType,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { skipToken } from "@reduxjs/toolkit/query";
import { getConversionMinCost } from "@shared/features/trades/common/utils";
import {
  useClientsData,
  useExchangesMetadata,
  useRefValue,
} from "@shared/utils/hooks/misc";
import { useTheme } from "styled-components";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { QuotaHandleTriggerClick } from "@shared/features/settings/types.ts";

interface OrderInfoProps {
  symbol?: string;
  account?: any;
  accountId?: number;
  isEntry: boolean;
  setOkButton: (button: ReactNode) => void;
  moveToPreviousStep: () => void;
  closeModal: () => void;
  isContract?: boolean;
  isReverse?: boolean;
  marginType?: MarginTypes;
  handleTriggerClick: QuotaHandleTriggerClick;
  quotaLoading: boolean;
}

const OrderInfo = ({
  symbol,
  accountId: propsAccountId,
  account: propsAccount,
  isEntry,
  setOkButton,
  moveToPreviousStep,
  closeModal,
  isContract,
  isReverse,
  marginType: propsMarginType,
  handleTriggerClick,
  quotaLoading,
}: OrderInfoProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { getIsFree } = useExchangesMetadata();
  const theme = useTheme();
  const { isDrawer } = useContext(DrawerContext) || {};
  const [getPortfolioData] =
    sharedLib.sharedApi.portfolio.useLazyGetPortfolioDataQuery();
  const [availableCost, setAvailableCost] = useState<number>();
  const [configuration, setConfiguration] = useState<ConfigurationItem>();
  const [createOrder, { isLoading: createOrderLoading }] =
    sharedLib.sharedApi.portfolio.useCreateOrderMutation();
  const {
    generalConfiguration,
    getItemValueByName,
    getItemByName,
    getItemShouldDisplay,
  } = useGeneralConfiguration({
    configuration,
    showOverrides: false,
    colorModifiedValues: false,
  });
  const { getAccountById } = useClientsData();
  const account =
    propsAccount ?? (propsAccountId && getAccountById(propsAccountId));
  const accountId = account?.id ?? propsAccountId;

  const leverageItem = getItemByName("multiplier");
  const leverageValue = leverageItem?.value;
  const hideLeverageItem = leverageItem && !getItemShouldDisplay(leverageItem);
  const leverageValueForConversion = hideLeverageItem
    ? getItemMaxOptionValue(leverageItem)
    : leverageValue;

  const [selectedOrderType, setSelectedOrderType] = useState<OrderType>(
    OrderType.MARKET
  );
  const [selectedAmount, setSelectedAmount] = useState<string>();
  const [errors, setErrors] = useState<Record<string, boolean>>({
    [AMOUNT_ERROR]: false,
    [LIMIT_PRICE_ERROR]: false,
    [TRIGGER_PRICE_ERROR]: false,
  });
  const shortOrLong = getItemValueByName("long/short") as LongShort;
  const isShort =
    shortOrLong === LongShort.SHORT || (!!availableCost && availableCost < 0);
  const reverse = isReverse || isShort;
  const options = {
    refetchOnMountOrArgChange: true,
  };
  const getAvailableAmountQuery =
    sharedLib.sharedApi.common.useGetAvailableAmountQuery(
      isEntry && symbol && accountId
        ? { symbol: symbol || "", account_id: accountId || 0, reverse }
        : skipToken,
      options
    );
  const getAvailableQuantityQuery =
    sharedLib.sharedApi.common.useGetAvailableQuantityQuery(
      !isEntry && symbol && accountId
        ? { symbol: symbol || "", account_id: accountId || 0, reverse }
        : skipToken,
      options
    );
  const getAvailableAmountDataQuery = isEntry
    ? getAvailableAmountQuery
    : getAvailableQuantityQuery;
  const coin = getAvailableAmountDataQuery.data?.coin;
  const { data: generalInfo, isLoading: isLoadingGeneralInfo } =
    sharedLib.sharedApi.portfolio.useGetCreateOrderGeneralInfoQuery(
      symbol && accountId
        ? {
            entry_or_tp: isEntry
              ? CreateOrderEntryOrTakeProfit.ENTRY
              : CreateOrderEntryOrTakeProfit.TAKE_PROFIT,
            account_id: accountId,
            symbol,
            reverse,
          }
        : skipToken,
      options
    );

  const currentPrice = generalInfo?.data.current_price || 0;
  const [selectedLimitPrice, setSelectedLimitPrice] = useState<string>("");
  const [selectedTriggerPrice, setSelectedTriggerPrice] = useState<string>("");
  const { getPrecision } = usePrecision(undefined, generalInfo?.precision);
  const amountPrecision = getPrecision(PrecisionType.AMOUNT, coin);
  const quantityPrecision = getPrecision(PrecisionType.QUANTITY, coin);
  const costPrecision = isEntry ? amountPrecision : quantityPrecision;
  const conversionMinCost = useMemo(
    () =>
      getConversionMinCost(
        isEntry,
        leverageValueForConversion || 1,
        amountPrecision,
        quantityPrecision,
        currentPrice,
        generalInfo?.amount_conversions,
        generalInfo?.limits?.amount?.min,
        generalInfo?.limits?.quantity?.min
      ),
    [isEntry, leverageValueForConversion, generalInfo]
  );
  const pricePrecision = getPrecision(PrecisionType.PRICE, coin);

  const absAvailableCost = Math.abs(availableCost ?? 0);
  const showStopMarket = generalInfo?.data.stop_market;

  useEffect(() => {
    if (!configuration) {
      setConfiguration(generalInfo?.configuration);
    }
  }, [generalInfo?.configuration]);

  useEffect(() => {
    if (isEntry && getAvailableAmountQuery?.data) {
      setAvailableCost(getAvailableAmountQuery.data?.data.balance.amount);
    }
  }, [getAvailableAmountQuery?.data]);

  useEffect(() => {
    if (!isEntry && getAvailableQuantityQuery?.data) {
      setAvailableCost(getAvailableQuantityQuery.data?.data.balance.quantity);
    }
  }, [getAvailableQuantityQuery?.data]);

  const loading = getAvailableAmountDataQuery.isLoading || isLoadingGeneralInfo;
  const moveToPreviousStepRef = useRefValue(moveToPreviousStep);
  useEffect(() => {
    if (loading || availableCost === undefined || isNaN(conversionMinCost))
      return;
    if (absAvailableCost < conversionMinCost) {
      openMessage(
        t("create_order.not_enough_available", {
          coin: !isEntry && isContract ? symbol : coin,
        }),
        "error"
      );
      moveToPreviousStepRef.current();
    }
  }, [absAvailableCost, conversionMinCost, loading]);

  useEffect(() => {
    const price = toFixed(currentPrice, pricePrecision).toString();
    setSelectedLimitPrice(price);
    setSelectedTriggerPrice(price);
  }, [currentPrice]);

  const handleSetError = (key: string, value: boolean) => {
    if (errors[key] !== value) {
      setErrors({
        ...errors,
        [key]: value,
      });
    }
  };

  const marginType = propsMarginType ?? getItemValueByName("Margin Type");
  const showLimitPriceInput =
    [STOP_LIMIT_ORDER_TYPE, LIMIT_ORDER_TYPE].indexOf(selectedOrderType) >= 0;
  const showTriggerPriceInput =
    [STOP_LIMIT_ORDER_TYPE, STOP_MARKET_ORDER_TYPE].indexOf(
      selectedOrderType
    ) >= 0;

  const handleCreateOrder = () => {
    if (!account || !symbol) return;
    const data: CreateOrderData = {
      account_id: accountId,
      symbol: symbol,
      entry_or_tp: isEntry ? ENTRY_ORDER : TAKE_PROFIT_ORDER,
      order_type: selectedOrderType,
      multiplier: hideLeverageItem ? 0 : leverageValue,
      long_short: isShort ? LongShort.SHORT : LongShort.LONG,
      cost: parseFloat(selectedAmount || ""),
      margin_type: marginType,
    };
    if (showLimitPriceInput) {
      data.limit_price = Number(selectedLimitPrice);
    }
    if (showTriggerPriceInput) {
      data.stop_price = Number(selectedTriggerPrice);
    }
    displayResultMessage(
      createOrder(data).unwrap(),
      t("create_order.success_message"),
      t("create_order.error_message")
    )
      .then(() => {
        getPortfolioData({ use_cache: false });
        closeModal();
      })
      .catch(() => {});
  };

  const onCreateOrderClick = () => {
    if (getIsFree(account?.exchange)) {
      handleCreateOrder();
    } else {
      handleTriggerClick().then((isQuotaError) => {
        if (!isQuotaError) {
          handleCreateOrder();
        }
      });
    }
  };

  const errorsExist = () => Object.values(errors).some((error) => error);

  useEffect(() => {
    if (loading) return;
    const button = (
      <Button
        type="primary"
        onClick={onCreateOrderClick}
        disabled={errorsExist()}
        loading={createOrderLoading || quotaLoading}
        style={{ width: "100%" }}
      >
        {t("create_order.create")}
      </Button>
    );
    setOkButton(button);
  }, [
    selectedAmount,
    selectedTriggerPrice,
    selectedLimitPrice,
    shortOrLong,
    leverageValue,
    selectedOrderType,
    errors,
    createOrderLoading,
    hideLeverageItem,
    loading,
    marginType,
    quotaLoading,
  ]);

  return (
    <Flex
      $flexDirection="column"
      $justifyContent="flex-start"
      style={WRAPPER_STYLE}
    >
      <LoadingSpinnerOverlay
        isLoading={loading}
        hideContentWhileLoading={loading}
        backgroundColor={
          isDrawer ? theme.colors.layoutBgColor : theme.colors.cardBgColor
        }
        spinnerSize="large"
      >
        <Space
          size={20}
          orientation="vertical"
          style={{
            width: "100%",
            marginBottom: isDrawer ? 40 : 0,
          }}
        >
          {account && (
            <SymbolAndExchangeSummary
              symbol={symbol}
              exchange={account.exchange}
            />
          )}
          <GeneralInfoBoxes
            availableCost={absAvailableCost}
            symbol={coin}
            pair={symbol}
            isEntry={isEntry}
            generalInfo={generalInfo}
          />
          <OrderTypeSelector
            showStopMarket={showStopMarket}
            handleSelectOrderType={setSelectedOrderType}
            selectedOrderType={selectedOrderType}
          />
          {generalConfiguration}
          <AmountInput
            coin={coin}
            setError={(value: boolean) => handleSetError(AMOUNT_ERROR, value)}
            isEntry={isEntry}
            error={errors[AMOUNT_ERROR]}
            setSelectedAmount={setSelectedAmount}
            selectedAmount={selectedAmount}
            amountPrecision={costPrecision}
            conversionMinCost={conversionMinCost}
            availableCost={absAvailableCost}
          />
          {showTriggerPriceInput && (
            <TriggerPriceInput
              triggerPrice={selectedTriggerPrice}
              handlePriceChange={setSelectedTriggerPrice}
              setError={(value: boolean) =>
                handleSetError(TRIGGER_PRICE_ERROR, value)
              }
              isShort={isShort}
              error={errors[TRIGGER_PRICE_ERROR]}
              pricePrecision={pricePrecision}
              isEntry={isEntry}
              exchange={account.exchange}
              generalInfo={generalInfo}
            />
          )}
          {showLimitPriceInput && (
            <LimitPriceInput
              limitPrice={selectedLimitPrice}
              handlePriceChange={setSelectedLimitPrice}
              setError={(value: boolean) =>
                handleSetError(LIMIT_PRICE_ERROR, value)
              }
              error={errors[LIMIT_PRICE_ERROR]}
              pricePrecision={pricePrecision}
              exchange={account.exchange}
              generalInfo={generalInfo}
            />
          )}
        </Space>
      </LoadingSpinnerOverlay>
    </Flex>
  );
};

export default OrderInfo;

const WRAPPER_STYLE: React.CSSProperties = {
  position: "relative",
  padding: "0 20px",
  width: "100%",
  overflow: "auto",
};
